import React, { useEffect, useMemo, useState } from "react";
import { Table, DatePicker, message, Card, Tag, Button, Select, Space } from "antd";
import PageBreadcrumb from "../../component/PageBreadcrumb";
import { GetPsuedoSlotRequestSummary } from "../../redux/actions/SessionAction";
import moment from "moment-timezone";

const { RangePicker } = DatePicker;

// Helpers using moment for IST formatting
const formatISTDateTime = (iso) => (iso ? moment(iso).tz("Asia/Kolkata").format("YYYY-MM-DD HH:mm") : "-");
const formatISTDateOnly = (iso) => (iso ? moment(iso).tz("Asia/Kolkata").format("YYYY-MM-DD") : "-");
const formatISTTimeFromDateAndHHmm = (dateIsoOrYmd, hhmm) => {
  if (!dateIsoOrYmd || !hhmm) return "-";
  const ymd = dateIsoOrYmd.length > 10 ? dateIsoOrYmd.slice(0, 10) : dateIsoOrYmd;
  return moment.utc(`${ymd}T${hhmm}`).tz("Asia/Kolkata").format("HH:mm");
};

const PsuedoSlotSummary = () => {
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({ totalDocs: 0, currentPage: 1, limit: 20 });
  const [dateRange, setDateRange] = useState(() => [moment().startOf("month"), moment()]);
  const [rangeType, setRangeType] = useState("weekly"); 

  const columns = useMemo(() => [
    { title: "#", dataIndex: "index", width: 60 },
    { title: "Status", dataIndex: "status", render: (t) => <Tag color={t === 'accepted' ? 'green' : t?.includes('cancel') ? 'red' : 'blue'}>{t}</Tag> },
    { title: "Slot Type", dataIndex: "slotType" },
    { title: "Learner Name", dataIndex: "learnerName" },
    { title: "Phone", dataIndex: "phoneNumber" },
    { title: "Teacher", dataIndex: "teacherName" },
    { title: "Created At", dataIndex: "createdAt" },
    { title: "Accepted At", dataIndex: "acceptedAt" },
    { title: "Session Date", dataIndex: "sessionDate" },
    { title: "startTime", dataIndex: "startTime" },
    { title: "endTime", dataIndex: "endTime" },
  ], []);

  const fetchData = (page = 1, limit = pagination.limit) => {
    const isCustom = rangeType === "custom";
    const startDate = isCustom && dateRange?.[0] ? dateRange[0].format("YYYY-MM-DD") : undefined;
    const endDate = isCustom && dateRange?.[1] ? dateRange[1].format("YYYY-MM-DD") : undefined;
    if (isCustom && (!startDate || !endDate)) return;
    setLoading(true);
    GetPsuedoSlotRequestSummary(
      isCustom ? { startDate, endDate, page, limit } : { range: rangeType, page, limit },
      (res) => {
        const formatted = (res?.data || []).map((item, idx) => ({
          key: item._id,
          index: (page - 1) * limit + idx + 1,
          status: item.status,
          slotType: item.slotType,
          learnerName: item?.learner?.name,
          phoneNumber: item?.learner?.phoneNumber,
          teacherName: item?.selectedTeacher?.name || "-",
          createdAt: formatISTDateTime(item?.createdAt),
          acceptedAt: formatISTDateTime(item?.acceptedAt),
          sessionDate: formatISTDateOnly(item?.sessionDateTime || item?.sessionData?.sessionDate),
          startTime: item?.sessionDateTime
            ? moment.utc(item.sessionDateTime).tz("Asia/Kolkata").format("HH:mm")
            : item?.startTime
            ? moment.utc(item.startTime).tz("Asia/Kolkata").format("HH:mm")
            : formatISTTimeFromDateAndHHmm(item?.sessionData?.sessionDate, item?.sessionData?.startTime),
          endTime: item?.endTime
            ? moment.utc(item.endTime).tz("Asia/Kolkata").format("HH:mm")
            : item?.sessionDateTime
            ? moment.utc(item.sessionDateTime).add(15, 'minute').tz("Asia/Kolkata").format("HH:mm")
            : formatISTTimeFromDateAndHHmm(item?.sessionData?.sessionDate, item?.sessionData?.endTime),
        }));
        setRows(formatted);
        setPagination({
          totalDocs: res?.pagination?.totalDocs || formatted.length,
          currentPage: res?.pagination?.currentPage || page,
          limit: res?.pagination?.limit || limit,
        });
        setLoading(false);
      },
      (errMsg) => {
        message.error(errMsg || "Failed to load summary");
        setLoading(false);
      }
    );
  };

  const buildCsv = (items) => {
    const headers = [
      "Status",
      "Slot Type",
      "Booking Route",
      "Learner Name",
      "Learner Email",
      "Learner Phone",
      "Teacher",
      "Teacher Demo %",
      "Created At (IST)",
      "Accepted At (IST)",
      "Session Date (IST)",
      "Start (IST)",
      "End (IST)",
    ];

    const escape = (val) => {
      if (val === undefined || val === null) return "";
      const s = String(val);
      if (s.includes(',') || s.includes('"') || s.includes('\n')) {
        return '"' + s.replace(/"/g, '""') + '"';
      }
      return s;
    };

    const lines = [headers.join(",")];
    items.forEach((item) => {
      const line = [
        escape(item.status),
        escape(item.slotType),
        escape(item.bookingRoute || "-"),
        escape(item.learner?.name),
        escape(item.learner?.email),
        escape(item.learner?.phoneNumber),
        escape(item.selectedTeacher?.name || "-"),
        escape(item.selectedTeacher?.demoConversionPercentage ?? "-"),
        escape(formatISTDateTime(item.createdAt)),
        escape(formatISTDateTime(item.acceptedAt)),
        escape(formatISTDateOnly(item.sessionDateTime || item.sessionData?.sessionDate)),
        escape(item.startTime ? moment(item.startTime).tz("Asia/Kolkata").format("HH:mm") : formatISTTimeFromDateAndHHmm(item.sessionData?.sessionDate, item.sessionData?.startTime)),
        escape(item.endTime ? moment(item.endTime).tz("Asia/Kolkata").format("HH:mm") : formatISTTimeFromDateAndHHmm(item.sessionData?.sessionDate, item.sessionData?.endTime)),
      ];
      lines.push(line.join(","));
    });
    return lines.join("\n");
  };

  const downloadCsv = async () => {
    const isCustom = rangeType === "custom";
    const start = isCustom ? dateRange?.[0]?.format("YYYY-MM-DD") : undefined;
    const end = isCustom ? dateRange?.[1]?.format("YYYY-MM-DD") : undefined;
    if (isCustom && (!start || !end)) return;
    setDownloading(true);
    try {
      let page = 1;
      const limit = 200;
      let allItems = [];
      let totalPages = 1;
      do {
        // eslint-disable-next-line no-await-in-loop
        const chunk = await new Promise((resolve, reject) => {
          GetPsuedoSlotRequestSummary(
            isCustom ? { startDate: start, endDate: end, page, limit } : { range: rangeType, page, limit },
            (res) => resolve(res),
            (err) => reject(err)
          );
        });
        totalPages = chunk?.pagination?.totalPages || 1;
        allItems = allItems.concat(chunk?.data || []);
        page += 1;
      } while (page <= totalPages);

      const csv = buildCsv(allItems);
      const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const fileSuffix = isCustom ? `${start}_to_${end}` : rangeType;
      a.download = `psuedo-slot-summary_${fileSuffix}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      message.error("Failed to download CSV");
    } finally {
      setDownloading(false);
    }
  };

  useEffect(() => {
    // On mount, auto-fetch with default weekly
    fetchData(1, pagination.limit);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div>
      <PageBreadcrumb route={["dashboard", "Psuedo slot summary"]} />
      <Card style={{ marginBottom: 16 }}>
        <Space wrap>
          <Select value={rangeType} onChange={(v) => setRangeType(v)} style={{ width: 160 }}>
            <Select.Option value="custom">Custom</Select.Option>
            <Select.Option value="daily">Daily</Select.Option>
            <Select.Option value="weekly">Weekly</Select.Option>
            <Select.Option value="monthly">Monthly</Select.Option>
          </Select>
          {rangeType === "custom" && (
            <>
              <span>Date range:</span>
              <RangePicker
                value={dateRange}
                onChange={(vals) => {
                  if (!vals || !vals[0] || !vals[1]) return;
                  setDateRange(vals);
                }}
                allowClear={false}
              />
            </>
          )}
          <Button onClick={() => fetchData(1, pagination.limit)} disabled={loading}>
            Fetch
          </Button>
          <Button type="primary" onClick={downloadCsv} loading={downloading} disabled={loading}>
            Download CSV
          </Button>
        </Space>
      </Card>
      <Table
        loading={loading}
        columns={columns}
        dataSource={rows}
        pagination={{
          total: pagination.totalDocs,
          current: pagination.currentPage,
          pageSize: pagination.limit,
          showSizeChanger: true,
          onChange: (p, ps) => fetchData(p, ps),
          showTotal: (t) => `${t} items`,
        }}
        scroll={{ x: true }}
      />
    </div>
  );
};

export default PsuedoSlotSummary;
